import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Button, Form, Row, Col, Alert } from 'react-bootstrap';
import { useForm } from 'react-hook-form';

const round2 = (n) => Number((Number(n) || 0).toFixed(2));
const clamp0 = (n) => Math.max(0, Number(n) || 0);

export default function ZellePayModal({ show, onHide, onPay, zelle }) {
  const { register, handleSubmit, reset, watch, setValue } = useForm({
    defaultValues: { paidUSD: 0, paidHTG: 0, paymentNote: '' },
  });
  const [autoCalc, setAutoCalc] = useState(true);
  const lastChanged = useRef('HTG');
  const grossUSD = Number(zelle?.amountUSD || 0);
  const feeUSD = Number(zelle?.feeUSD || 0);
  const expectedUSD = round2(Math.max(0, grossUSD - feeUSD));
  const rate = Number(zelle?.rateUsdHtg || 0);
  const financialReady = grossUSD > 0 && rate > 0 && zelle?.amountHTG != null;

  useEffect(() => {
    if (!show) return;
    reset({ paidUSD: 0, paidHTG: financialReady ? round2(Number(zelle?.amountHTG || 0)) : 0, paymentNote: zelle?.paymentNote || '' });
    setAutoCalc(true); lastChanged.current = 'HTG';
  }, [show, reset, zelle, financialReady]);

  const paidUSD = round2(watch('paidUSD'));
  const paidHTG = round2(watch('paidHTG'));
  const paidEquivalentUSD = useMemo(() => rate > 0 ? round2(paidUSD + paidHTG / rate) : 0, [paidUSD, paidHTG, rate]);
  const diff = round2(Math.abs(paidEquivalentUSD - expectedUSD));
  const canSubmit = financialReady && diff <= 0.01 && paidUSD >= 0 && paidHTG >= 0;

  useEffect(() => {
    if (!show || !autoCalc || !financialReady) return;
    if (lastChanged.current === 'HTG') {
      const nextUSD = clamp0(round2(expectedUSD - paidHTG / rate));
      if (Math.abs(nextUSD - paidUSD) > 0.009) setValue('paidUSD', nextUSD);
    } else {
      const nextHTG = clamp0(round2((expectedUSD - paidUSD) * rate));
      if (Math.abs(nextHTG - paidHTG) > 0.009) setValue('paidHTG', nextHTG);
    }
  }, [show, autoCalc, financialReady, expectedUSD, paidHTG, paidUSD, rate, setValue]);

  const submit = (vals) => {
    if (!canSubmit) return;
    onPay({ paidUSD: round2(vals.paidUSD), paidHTG: round2(vals.paidHTG), paymentNote: String(vals.paymentNote || '').trim() || null });
  };

  return (
    <Modal show={show} onHide={onHide} backdrop="static" size="lg">
      <Modal.Header closeButton><Modal.Title>Payer la transaction Zelle</Modal.Title></Modal.Header>
      <Form onSubmit={handleSubmit(submit)}>
        <Modal.Body>
          <Row className="g-2 mb-3">
            <Col md={6}><div className="text-muted small">Sender</div><strong>{zelle?.senderName || '—'}</strong></Col>
            <Col md={6}><div className="text-muted small">Receiver</div><strong>{zelle?.beneficiaryName || '—'}</strong></Col>
            <Col md={6}><div className="text-muted small">Code confirmation</div><strong>{zelle?.confirmationCode || '—'}</strong></Col>
            <Col md={6}><div className="text-muted small">Taux</div><strong>{rate > 0 ? `${rate.toFixed(2)} HTG` : '—'}</strong></Col>
          </Row>
          {!financialReady && <Alert variant="warning">Le montant financier de cette Zelle n’est pas encore complet. Fermez cette fenêtre, utilisez « Modifier », complétez le montant, puis revenez au paiement.</Alert>}
          {financialReady && <Alert variant="secondary">Net à remettre : <strong>{expectedUSD.toFixed(2)} USD</strong> / {Number(zelle?.amountHTG || 0).toFixed(2)} HTG.</Alert>}

          <Form.Check type="switch" label="Calcul automatique du reste" checked={autoCalc} onChange={(e) => setAutoCalc(e.target.checked)} className="mb-3" />
          <Row className="g-3">
            <Col md={6}><Form.Group><Form.Label>Payé USD</Form.Label><Form.Control type="number" min="0" step="0.01" disabled={!financialReady} {...register('paidUSD')} onChange={(e) => { lastChanged.current = 'USD'; setValue('paidUSD', e.target.value); }} /></Form.Group></Col>
            <Col md={6}><Form.Group><Form.Label>Payé HTG</Form.Label><Form.Control type="number" min="0" step="0.01" disabled={!financialReady} {...register('paidHTG')} onChange={(e) => { lastChanged.current = 'HTG'; setValue('paidHTG', e.target.value); }} /></Form.Group></Col>
            <Col md={12}><Form.Group><Form.Label>Note au moment du paiement</Form.Label><Form.Control as="textarea" rows={3} {...register('paymentNote')} /></Form.Group></Col>
          </Row>
          {financialReady && <div className={`small mt-3 ${diff <= 0.01 ? 'text-success' : 'text-danger'}`}>Équivalent payé : {paidEquivalentUSD.toFixed(2)} USD • Écart : {diff.toFixed(2)} USD</div>}
        </Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={onHide}>Annuler</Button><Button type="submit" variant="success" disabled={!canSubmit}>Confirmer le paiement</Button></Modal.Footer>
      </Form>
    </Modal>
  );
}
